import {
  Component,
  ElementRef,
  AfterViewInit,
  EventEmitter,
  OnDestroy,
  ChangeDetectionStrategy,
  Renderer2,
} from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { delay, takeUntil, skip } from 'rxjs/operators';

import { GasPositioning } from '../../utils/gas-positioning/gas-positioning.namespace';
import { GasClickOutsideDirective } from '../../utils/click-outside-directive/gas-click-outside.directive';

@Component({
  selector: 'gas-dynamic-base',
  templateUrl: './gas-dynamic-base.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasDynamicBaseComponent<T> implements AfterViewInit, OnDestroy {
  public closeComponentSubject$ = new Subject<void | T>();

  public closeComponent$: Observable<void | T> = this.closeComponentSubject$
    .pipe(
      delay(100),
    );

  public render$ = new EventEmitter<void>();
  public clickOutside$ = new EventEmitter<void>();
  public destroyed$ = new Subject<void>();
  public hovered$ = new EventEmitter<boolean>();
  public hoveredMainElement$ = new BehaviorSubject<boolean>(false);
  public hoveredInnerElement$ = new BehaviorSubject<boolean>(false);

  private previousPosition: GasPositioning.Direction;

  public set alignedPosition(direction: GasPositioning.Direction) {
    if (this.previousPosition !== direction) {
      setTimeout(() => {
        const element: HTMLElement = this.el.nativeElement;

        if (!this.previousPosition) {
          this.previousPosition = direction;
        } else {
          this.renderer.removeClass(element, `_${this.previousPosition}`);
        }

        this.renderer.addClass(element, `_${direction}`);
      });
    }
  }

  constructor(
    public el: ElementRef,
    public gasClickOutsideDirective: GasClickOutsideDirective,
    public renderer: Renderer2,
  ) {
    this.gasClickOutsideDirective?.handleClick$
      .pipe(
        skip(1),
        takeUntil(
          this.destroyed$,
        ),
      )
      .subscribe(() => this.clickOutside$.emit());

    this.hoveredMainElement$.asObservable()
      .pipe(
        takeUntil(this.destroyed$),
        skip(1),
        delay(100),
      )
      .subscribe((isHovered: boolean) => {
        if (!isHovered && !this.hoveredInnerElement$.getValue()) {
          this.hovered$.emit(false);
        }
      });
  }

  public onMouseEnter(): void {
    this.hoveredInnerElement$.next(true);

    this.hovered$.emit(true);
  }

  public onMouseLeave(): void {
    this.hoveredInnerElement$.next(false);

    if (!this.hoveredMainElement$.getValue()) {
      this.hovered$.emit(false);
    }
  }

  public ngAfterViewInit(): void {
    this.render$.next();

    const element: HTMLElement = this.el.nativeElement;

    element.onmouseenter = () => this.onMouseEnter();
    element.onmouseleave = () => this.onMouseLeave();
  }

  public close(payload: T): void {
    this.closeComponentSubject$.next(payload);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
